import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Badge, Field, Input, Textarea, LoadingState, ErrorState, GradientBar, Logo } from "@/components/design-system";
import { getPublicCampaign, submitFeedback } from "@/lib/campaigns";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

const cn = (...xs) => xs.filter(Boolean).join(" ");

function LikertField({ q, value, onChange, testId }) {
  return (
    <div>
      <div className="text-sm font-semibold text-[#0B1F3A]">{q.text}{q.required && <span className="text-red-500"> *</span>}</div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {[1,2,3,4,5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`${testId}-${n}`}
            onClick={() => onChange(n)}
            className={cn("w-11 h-11 rounded-lg font-bold border-2 transition-colors", value === n ? "bg-[#2D7FF9] text-white border-[#2D7FF9]" : "bg-white text-[#0B1F3A]/70 border-[#0B1F3A]/15 hover:border-[#2D7FF9]")}
          >{n}</button>
        ))}
      </div>
      {q.labels && (
        <div className="mt-2 flex justify-between text-[10px] uppercase tracking-widest text-[#0B1F3A]/55">
          <span>1 · {q.labels["1"]}</span><span>5 · {q.labels["5"]}</span>
        </div>
      )}
    </div>
  );
}

function SingleField({ q, value, onChange, testId }) {
  return (
    <div>
      <div className="text-sm font-semibold text-[#0B1F3A]">{q.text}{q.required && <span className="text-red-500"> *</span>}</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {q.options.map((opt) => (
          <button
            key={opt}
            type="button"
            data-testid={`${testId}-${opt.slice(0,10)}`}
            onClick={() => onChange(opt)}
            className={cn("px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors", value === opt ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "bg-white text-[#0B1F3A]/70 border-[#0B1F3A]/15 hover:border-[#0B1F3A]/40")}
          >{opt}</button>
        ))}
      </div>
    </div>
  );
}

export default function CampaignSubmit() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, checking } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [state, setState] = useState({ status: "loading", err: "" });
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const c = await getPublicCampaign(id);
        setCampaign(c);
        setState({ status: "ready", err: "" });
      } catch (err) {
        const s = err?.response?.status;
        setState({ status: s === 404 ? "notfound" : "error", err: formatApiError(err) });
      }
    })();
  }, [id]);

  const setA = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await submitFeedback(id, answers);
      setDone(true);
    } catch (err) { setError(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (checking || state.status === "loading") return (
    <div className="min-h-screen flex items-center justify-center bg-[#E8F1FF]"><LoadingState label="Loading campaign…" /></div>
  );

  return (
    <div className="min-h-screen bg-white text-[#0B1F3A]">
      <header className="border-b border-[#0B1F3A]/10">
        <div className="max-w-3xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/"><Logo variant="light" height={54} /></Link>
          <div className="flex items-center gap-3">
            {user ? <Link to="/app"><Button variant="ghost">Open app</Button></Link> : <Link to="/login"><Button variant="ghost">Log in</Button></Link>}
          </div>
        </div>
        <GradientBar />
      </header>
      <main className="max-w-3xl mx-auto px-6 py-14">
        {state.status === "notfound" && (
          <div className="text-center py-16">
            <h1 className="font-[Montserrat] font-extrabold text-3xl">Campaign not available.</h1>
            <p className="text-[#0B1F3A]/70 mt-3">This campaign is either private or has been closed.</p>
            <Link to="/" className="text-[#2D7FF9] font-semibold mt-4 inline-block">Back to home →</Link>
          </div>
        )}

        {state.status === "ready" && campaign && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={campaign.status === "open" ? "active" : "neutral"}>{campaign.status}</Badge>
              <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">{campaign.template_name}</span>
            </div>
            <h1 data-testid="campaign-submit-title" className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight mt-2">{campaign.name}</h1>
            <p className="mt-2 text-[#0B1F3A]/75">Giving feedback for <Link className="text-[#2D7FF9] font-semibold" to={`/p/${campaign.product_slug}`}>{campaign.product_name}</Link>{campaign.founder_name ? <> by <b>{campaign.founder_name}</b></> : null}.</p>
            {campaign.description && <p className="mt-4 text-[#0B1F3A]/80 leading-relaxed">{campaign.description}</p>}

            {done ? (
              <Card className="mt-10" data-testid="campaign-submit-thankyou">
                <h2 className="font-[Montserrat] font-bold text-2xl text-[#0B1F3A]">Thank you — your feedback is in.</h2>
                <p className="mt-3 text-[#0B1F3A]/75">The founder can now see your structured answers. When feedback is cited in the AI insight report (Cycle C4), your reputation goes up. Keep an eye on <Link className="text-[#2D7FF9] font-semibold" to="/app/my-feedback">My feedback</Link>.</p>
                <div className="mt-6 flex gap-3">
                  <Link to="/app/explore"><Button variant="primary">Explore more products</Button></Link>
                  <Link to="/app/my-feedback"><Button variant="secondary">View my submissions</Button></Link>
                </div>
              </Card>
            ) : !user ? (
              <Card className="mt-10">
                <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Sign in to give feedback</h2>
                <p className="mt-2 text-sm text-[#0B1F3A]/75">Feedback is tied to your community profile so your reputation grows with every helpful review.</p>
                <div className="mt-4 flex gap-3">
                  <Link to={`/login?next=${encodeURIComponent(`/campaign/${id}`)}`}><Button variant="primary">Log in</Button></Link>
                  <Link to={`/register?role=community`}><Button variant="secondary">Create a community account</Button></Link>
                </div>
              </Card>
            ) : campaign.status !== "open" ? (
              <Card className="mt-10"><p className="text-sm text-[#0B1F3A]/80">This campaign isn't accepting responses right now.</p></Card>
            ) : (
              <form onSubmit={onSubmit} className="mt-10 space-y-6" data-testid="campaign-submit-form">
                {campaign.questions.map((q, i) => (
                  <Card key={q.id}>
                    <div className="text-[11px] uppercase tracking-widest text-[#2D7FF9] font-bold">Question {i+1} of {campaign.questions.length}</div>
                    <div className="mt-2">
                      {q.type === "likert5" && <LikertField q={q} value={answers[q.id]} onChange={(v) => setA(q.id, v)} testId={`answer-${q.id}`} />}
                      {q.type === "single" && <SingleField q={q} value={answers[q.id]} onChange={(v) => setA(q.id, v)} testId={`answer-${q.id}`} />}
                      {q.type === "short" && (
                        <>
                          <div className="text-sm font-semibold text-[#0B1F3A]">{q.text}{q.required && <span className="text-red-500"> *</span>}</div>
                          <div className="mt-2"><Input data-testid={`answer-${q.id}`} maxLength={240} value={answers[q.id] || ""} onChange={(e) => setA(q.id, e.target.value)} /></div>
                        </>
                      )}
                      {q.type === "long" && (
                        <>
                          <div className="text-sm font-semibold text-[#0B1F3A]">{q.text}{q.required && <span className="text-red-500"> *</span>}</div>
                          <div className="mt-2"><Textarea data-testid={`answer-${q.id}`} rows={5} maxLength={2000} value={answers[q.id] || ""} onChange={(e) => setA(q.id, e.target.value)} /></div>
                          <div className="mt-1 text-xs text-[#0B1F3A]/55 text-right">{(answers[q.id] || "").length}/2000</div>
                        </>
                      )}
                    </div>
                  </Card>
                ))}
                {error && <p data-testid="campaign-submit-error" className="text-sm font-medium text-red-600">{error}</p>}
                <div className="flex items-center gap-3">
                  <Button data-testid="campaign-submit-btn" type="submit" variant="primary" disabled={submitting}>{submitting ? "Submitting…" : "Submit feedback"}</Button>
                  <Link to={`/p/${campaign.product_slug}`}><Button type="button" variant="ghost">Cancel</Button></Link>
                </div>
              </form>
            )}
          </>
        )}
      </main>
    </div>
  );
}
